// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — the rail-bottom PROFILE control: the persona-SWITCH affordance.
//
// BANNER-IDENTITY FIX: this no longer floats under/over the classification banner.
// It is re-homed to the BOTTOM of the left rail (the leftGutter Profile position)
// as a compact "persona · clearance" chip that opens the persona popover. The
// resident identity READOUT now lives in THIS popover (no persistent lower-left HUD);
// it is also the SWITCH affordance. The popover carries the full persona DETAIL + switch (a
// REAL re-login via /idp/token, which re-filters the whole picture for the new
// clearance) + sign-out — summoned on click, never resident over the picture. The
// same actions remain reachable from the ⌘K command palette. The switch behavior
// and the PDP are UNCHANGED — only WHERE this renders moved.
//
// AVATAR + CALLSIGN: the trigger renders the operator AVATAR (Avatar.tsx — Google
// profile picture on federated sessions, deterministic initials disc otherwise) and the
// label honours the I8 identity prefs (user.callsign unless user.useRealName opts back
// into the real name). Display-only: the JWT username stays the security subject.

import { useEffect, useRef, useState } from "react";
import { tdflite, type DevIdentity } from "../host/tdflite.ts";
import { personaFor, displayNameFor } from "../host/personas.ts";
import { canImpersonate } from "./personaGate.ts";
import { Avatar } from "./Avatar.tsx";
import { getSettingsStore } from "../../../../packages/local-settings/src/index.ts";
import type { UserIdentitySettings } from "../../../../packages/local-settings/src/index.ts";

export function PersonaSwitcher({
  current,
  summary,
  onSwitch,
  onLogout,
  mode = "chip",
}: {
  current: string;
  summary: string;
  onSwitch: (username: string, password: string) => void;
  onLogout: () => void;
  /** Trigger affordance. "chip" = the original text chip (persona · clearance ⌄).
   *  "icon" = the 54px FUNCTION RAIL rail-bottom Profile button: a 40×40 teal person
   *  SVG (mock line 140). The persona popover + the real idplite re-login are
   *  IDENTICAL in both modes — only the trigger affordance changes. */
  mode?: "chip" | "icon";
}): JSX.Element {
  const [personas, setPersonas] = useState<DevIdentity[]>([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    tdflite.devIdentities().then(setPersonas).catch(() => setPersonas([]));
  }, []);

  // The persisted session — carries the Google profile name/picture on federation logins
  // (host/tdflite.ts Session.name/.picture; preserved across silent refresh). Re-peeked when
  // the current identity changes (persona switch / re-login persists the new session first).
  const [session, setSession] = useState(() => tdflite.peek());
  useEffect(() => {
    setSession(tdflite.peek());
  }, [current]);

  // I8 identity display prefs (user.callsign / user.useRealName — identity-scoped leaves):
  // the chip label + avatar initials honour the operator's chosen handle, live.
  const [userPrefs, setUserPrefs] = useState<UserIdentitySettings>(
    () => getSettingsStore().getSettings().user,
  );
  useEffect(() => {
    const store = getSettingsStore();
    setUserPrefs(store.getSettings().user);
    return store.subscribe((s) => setUserPrefs(s.user));
  }, []);

  // Dismiss the popover on outside click or Escape — the chip is recessive; it must
  // never linger over the map.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const icon = mode === "icon";
  // Display name resolution — DISPLAY-ONLY; the username remains the security subject
  // (the JWT via the re-login is the real I5 rewrap subject).
  //   real name  = the Google profile name (federated session) else the roster displayName
  //                else the bare username;
  //   label      = the operator's CALLSIGN when set — unless "use real name" is opted in
  //                (or no callsign is set), in which case the real name shows.
  const sessionIsCurrent = session?.username === current;
  const realName = (sessionIsCurrent ? session?.name : undefined) ?? displayNameFor(current);
  const callsign = userPrefs.callsign.trim();
  const currentName = userPrefs.useRealName || callsign === "" ? realName : callsign;
  const currentRole = personaFor(current)?.role;
  // Google profile picture (federation logins only) for the avatar; initials disc otherwise.
  const picture = sessionIsCurrent ? session?.picture : undefined;

  return (
    <div className={`whoami${icon ? " whoami--icon" : ""}`} ref={rootRef}>
      {icon ? (
        // The 54px rail-bottom Profile button — now the operator AVATAR (Google profile
        // picture on federated sessions, deterministic initials disc otherwise) in place
        // of the old generic person SVG. The same popover summons on click; the re-login
        // path is unchanged.
        <button
          type="button"
          className="whoami__icon-btn"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Operator identity: ${currentName}, ${summary}. Switch persona.`}
          title={`Profile — ${currentName}. Switch persona.`}
          onClick={() => setOpen((v) => !v)}
        >
          <Avatar username={current} displayName={currentName} picture={picture} size={26} />
        </button>
      ) : (
        <button
          type="button"
          className="whoami__chip"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Operator identity: ${currentName}, ${summary}. Switch persona.`}
          onClick={() => setOpen((v) => !v)}
        >
          <Avatar username={current} displayName={currentName} picture={picture} size={18} />
          <span className="whoami__name">{currentName}</span>
          <span className="whoami__sep" aria-hidden="true">·</span>
          <span className="whoami__clearance">{summary}</span>
          <span className="whoami__caret" aria-hidden="true">⌄</span>
        </button>
      )}

      {open && (
        <div className="whoami__pop panel clearance" role="dialog" aria-label="Operator identity">
          <h2 className="panel__title">Operator · Identity</h2>
          <div className="clearance__whoami">
            <div className="whoami__id-row">
              <Avatar username={current} displayName={currentName} picture={picture} size={32} />
              <span className="clearance__whoami-name" title={realName}>{currentName}</span>
            </div>
            {currentRole && <span className="clearance__whoami-role">{currentRole}</span>}
            <span className="clearance__whoami-clearance">{summary}</span>
            <button type="button" className="clearance__logout" onClick={onLogout}>
              sign out
            </button>
          </div>
          {/* DEV-ONLY persona-impersonation roster. Gated on canImpersonate(personas): it renders
              ONLY when the dev-identities endpoint returned a roster (local/dev). After a REAL
              login in prod the endpoint is sealed (empty list) → only the current identity +
              sign-out above shows, never a roster to impersonate arbitrary clearances. */}
          {canImpersonate(personas) && (
            <>
              <div className="clearance__opts" role="radiogroup" aria-label="Switch persona">
                {personas.map((p) => {
                  const meta = personaFor(p.username);
                  return (
                    <button
                      key={p.username}
                      type="button"
                      className={`clearance__opt${p.username === current ? " is-active" : ""}`}
                      role="radio"
                      aria-checked={p.username === current}
                      onClick={() => {
                        onSwitch(p.username, p.password);
                        setOpen(false);
                      }}
                    >
                      <span className="clearance__label">{meta?.displayName ?? p.username}</span>
                      <span className="clearance__sub">
                        {meta?.role ? `${meta.role} · ${p.summary}` : p.summary}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="clearance__hint">
                Switch persona → the whole picture re-filters for that clearance (a real idplite re-login).
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
